import Vue from 'vue'
import VueRouter from 'vue-router'
import HomeView from '../views/HomeView.vue'
import AuthService from '@/services/auth'
import { hasExpired } from '@/utils/subscriptionHelper'
import store from '@/store'

Vue.use(VueRouter)

const routes = [
  // RUTAS PÚBLICAS (RAÍZ)
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/tienda',
    name: 'shop',
    component: () => import(/* webpackChunkName: "shop" */ '../views/ShopView.vue')
  },
  {
    path: '/producto/anillo',
    name: 'product-ring',
    component: () => import(/* webpackChunkName: "product-ring" */ '../views/ProductRingView.vue')
  },
  {
    path: '/producto/tarjeta',
    name: 'product-card',
    component: () => import(/* webpackChunkName: "product-card" */ '../views/ProductCardView.vue')
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import(/* webpackChunkName: "checkout" */ '../views/CheckoutView.vue')
    // No requiere auth - permite guest checkout
  },
  {
    path: '/checkout/success',
    name: 'checkout-success',
    component: () => import(/* webpackChunkName: "checkout-success" */ '../views/CheckoutSuccessView.vue')
    // Sin requiresAuth: Stripe regresa aquí también a invitados
  },
  {
    path: '/checkout/cancel',
    name: 'checkout-cancel',
    component: () => import(/* webpackChunkName: "checkout-cancel" */ '../views/CheckoutCancelView.vue')
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../views/LoginView.vue')
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import(/* webpackChunkName: "reset-password" */ '../views/ResetPasswordView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/setup-account',
    name: 'setup-account',
    component: () => import(/* webpackChunkName: "setup-account" */ '../views/SetupAccountView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/instrucciones',
    name: 'instructions',
    component: () => import(/* webpackChunkName: "instructions" */ '../views/InstructionsView.vue')
  },
  {
    path: '/aviso-privacidad',
    name: 'privacy-notice',
    component: () => import(/* webpackChunkName: "privacy-notice" */ '../views/PrivacyNoticeView.vue')
  },
  {
    path: '/terminos-condiciones',
    name: 'terms-conditions',
    component: () => import(/* webpackChunkName: "terms-conditions" */ '../views/TermsConditionsView.vue')
  },
  {
    path: '/website-expired',
    name: 'website-expired',
    component: () => import(/* webpackChunkName: "website-expired" */ '../views/WebsiteExpiredView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/form/:uuid',
    name: 'public-form',
    component: () => import(/* webpackChunkName: "public-form" */ '../views/PublicFormView.vue'),
    meta: { hideHeader: true }
  },
  {
    // Cliente frecuente: la tarjeta de sellos del cliente y la página del stand de mostrador
    path: '/sello/:alias',
    name: 'sello',
    component: () => import(/* webpackChunkName: "sello" */ '../views/SelloView.vue'),
    meta: { hideHeader: true }
  },
  {
    // «Cotiza y cobra»: la cotización que abre el cliente con su enlace
    path: '/cotizacion/:code',
    name: 'quote',
    component: () => import(/* webpackChunkName: "quote" */ '../views/CotizacionView.vue'),
    meta: { hideHeader: true }
  },
  {
    // «Dar sello»: la pantalla de quien atiende (el dueño con su sesión o un empleado con su acceso).
    // El QR de la tarjeta del cliente abre aquí con ?c=<código>
    path: '/dar-sello',
    name: 'give-stamp',
    component: () => import(/* webpackChunkName: "loyalty" */ '../views/DarSelloView.vue'),
    meta: { hideHeader: true }
  },
  {
    // El enlace de acceso de un empleado (solo dar sellos y canjear)
    path: '/acceso/:token',
    name: 'staff-access',
    component: () => import(/* webpackChunkName: "loyalty" */ '../views/AccesoView.vue'),
    meta: { hideHeader: true }
  },
  {
    // La pantalla del mostrador: el QR que cambia cada 30 s
    path: '/pantalla/:token',
    name: 'counter-screen',
    component: () => import(/* webpackChunkName: "sello" */ '../views/PantallaView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/mostrador/:alias',
    name: 'mostrador',
    component: () => import(/* webpackChunkName: "sello" */ '../views/MostradorView.vue'),
    meta: { hideHeader: true }
  },
  {
    // «Agenda sin faltas»: el enlace del recordatorio para confirmar, cambiar o cancelar
    path: '/cita/:token',
    name: 'cita',
    component: () => import(/* webpackChunkName: "cita" */ '../views/CitaView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/billing-expired',
    name: 'billing-expired',
    component: () => import(/* webpackChunkName: "billing-expired" */ '../views/BillingExpiredView.vue'),
    meta: { hideHeader: true }
  },

  // RUTAS PRIVADAS (DASHBOARD)
  {
    path: '/dashboard',
    component: { render: c => c('router-view') },
    meta: { requiresAuth: true },
    children: [
      {
        path: '', // /dashboard
        redirect: 'templates'
      },
      {
        path: 'templates',
        name: 'templates',
        component: () => import(/* webpackChunkName: "templates" */ '../views/TemplatesView.vue')
      },
      {
        path: 'create-nfcero-v2/:id?',
        name: 'CreateNFCeroV2',
        component: () => import('@/views/CreateNFCeroV2View.vue')
      },
      {
        path: 'create-nfcero/:id?',
        name: 'CreateNFCero',
        component: () => import('@/views/CreateNFCeroView.vue')
      },
      {
        path: 'analytics',
        name: 'analytics',
        component: () => import(/* webpackChunkName: "analytics" */ '../views/AnalyticsView.vue')
      },
      {
        path: 'form-data',
        name: 'form-data',
        component: () => import(/* webpackChunkName: "form-data" */ '../views/FormDataView.vue')
      },
      {
        // CRM («Clientes»), VIP: la pantalla y la API lo limitan al módulo «crm»
        path: 'clientes',
        name: 'crm',
        component: () => import(/* webpackChunkName: "crm" */ '../views/CrmView.vue')
      },
      {
        // Cliente frecuente (Premium y VIP)
        path: 'cliente-frecuente',
        name: 'loyalty',
        component: () => import(/* webpackChunkName: "loyalty" */ '../views/LoyaltyView.vue')
      },
      {
        // Catálogo y pedidos por WhatsApp (Premium y VIP)
        path: 'pedidos',
        name: 'orders',
        component: () => import(/* webpackChunkName: "orders" */ '../views/PedidosView.vue')
      },
      {
        // Catálogo: productos, precios y fotos (como Formularios); el perfil los muestra con la sección «Catálogo y pedidos»
        path: 'catalogo',
        name: 'catalogs',
        component: () => import(/* webpackChunkName: "catalogs" */ '../views/CatalogosView.vue')
      },
      {
        // «Dar sello» ahora vive en /dar-sello (lo usan también los empleados, sin la cuenta)
        path: 'dar-sello',
        redirect: to => ({ path: '/dar-sello', query: to.query })
      },
      {
        // Modo Evento (VIP)
        path: 'eventos',
        name: 'events',
        // Eventos vive dentro de Clientes (sección «Eventos»)
        redirect: { path: '/dashboard/clientes', query: { vista: 'eventos' } }
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import(/* webpackChunkName: "calendar" */ '../views/CalendarView.vue')
      },
      {
        path: 'file-manager',
        name: 'file-manager',
        component: () => import(/* webpackChunkName: "file-manager" */ '../views/FileManagerView.vue')
      },
      {
        path: 'extras',
        name: 'extras',
        component: () => import(/* webpackChunkName: "extras" */ '../views/ExtrasView.vue')
      },
      {
        path: 'support',
        name: 'support',
        component: () => import(/* webpackChunkName: "support" */ '../views/SupportView.vue')
      },
      {
        path: 'settings',
        component: () => import(/* webpackChunkName: "settings" */ '../views/SettingsView.vue'),
        children: [
          {
            path: '',
            redirect: 'profile'
          },
          {
            path: 'profile',
            name: 'settings-profile',
            component: () => import(/* webpackChunkName: "settings-profile" */ '../views/settings/ProfileView.vue'),
          },
          {
            path: 'billing',
            name: 'settings-billing',
            component: () => import(/* webpackChunkName: "settings-billing" */ '../views/settings/BillingView.vue'),
          },
          {
            path: 'password',
            name: 'settings-password',
            component: () => import(/* webpackChunkName: "settings-password" */ '../views/settings/PasswordView.vue'),
          }
        ]
      },
      {
        path: 'forms/:formId/submissions',
        name: 'form-submissions',
        component: () => import(/* webpackChunkName: "form-submissions" */ '../views/FormSubmissionsView.vue')
      }
    ]
  },

  // RUTA COMODÍN PARA SITIOS DE USUARIOS (AL FINAL)
  {
    path: '/:domain',
    name: 'website',
    component: () => import(/* webpackChunkName: "website" */ '../views/WebsiteView.vue'),
    meta: { hideHeader: true },
    beforeEnter: (to, from, next) => {
      const reservedRoutes = ['login', 'instrucciones', 'dashboard', 'static', 'img', 'css', 'js', 'setup-account', 'reset-password', 'form', 'tienda', 'producto', 'checkout', 'cita', 'sello', 'mostrador', 'dar-sello', 'acceso', 'pantalla', 'cotizacion']
      if (reservedRoutes.includes(to.params.domain)) {
        next({ name: 'home' })
      } else {
        next()
      }
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      // Auditoría N3: el enlace "suscripción" de los productos lleva directo a los planes
      return { selector: to.hash, offset: { x: 0, y: 70 } };
    } else {
      return { x: 0, y: 0 };
    }
  },
})

router.beforeEach((to, from, next) => {
  // ✅ /billing-expired está completamente pública, SIN VERIFICACIONES
  if (to.name === 'billing-expired') {
    next();
    return;
  }

  const isAuthenticated = AuthService.isAuthenticated();
  const isDashboardRoute = to.matched.some(record => record.path && record.path.includes('/dashboard'));
  const user = store.state.auth.user;

  // Si necesita autenticación
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      // No autenticado, ir a login
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      });
      return;
    }

    // Autenticado - verificar suscripción
    const subscriptionExpired = user && hasExpired(user);

    if (isDashboardRoute && subscriptionExpired) {
      // Dashboard pero expirado
      next({
        name: 'billing-expired'
      });
      return;
    }

    next();
  } else {
    // Ruta pública
    next();
  }
});

export default router